import React from 'react';

import { useFormContext } from 'react-hook-form';

import { z } from 'zod';

import AcGradientInput from 'components/AcGradientInput/AcGradientInput';
import Input from 'components/Input/Input';
import Switch from 'components/Switch/Switch';
import { FormControl, FormField, FormItem, FormMessage } from 'components/ui';

import { createColorSetValueHandler } from '../../../utils/blockForms.utils';
import { templateStudioSchema } from '../../../validation-schema';
import { DesignBlockMenuItem } from '../Components/DesignBlockMenuItem';

import './style.css';

export const TemplateStudioHeaderForm: React.FC = () => {
  const {
    control,
    setValue,
    watch,
    formState: { errors, touchedFields }
  } = useFormContext<z.infer<typeof templateStudioSchema>>();

  const colorSetValueHandler = (onChange: (value: any) => void) =>
    createColorSetValueHandler(
      'header.textColor',
      () => watch('header.textColor'),
      setValue as (name: string, value: any) => void,
      onChange
    );

  const hasTextColorError =
    touchedFields.header?.textColor && Boolean(errors.header?.textColor);
  const textColorHelperText = touchedFields.header?.textColor
    ? errors.header?.textColor?.toString()
    : '';

  return (
    <DesignBlockMenuItem blockType={'header'}>
      <FormField
        control={control}
        name="header.showLogo"
        render={({ field }) => (
          <Switch
            key={field.value ? 'on' : 'off'}
            label="Show Logo"
            textAlignment="left"
            defaultChecked={field.value}
            checked={field.value}
            onCheckedChange={field.onChange}
          />
        )}
      />
      <FormField
        control={control}
        name="header.showPlayerName"
        render={({ field }) => (
          <Switch
            key={field.value ? 'on' : 'off'}
            label="Show Player Name"
            textAlignment="left"
            defaultChecked={field.value}
            checked={field.value}
            onCheckedChange={field.onChange}
          />
        )}
      />
      <FormField
        control={control}
        name="header.showPlayerId"
        render={({ field }) => (
          <Switch
            key={field.value ? 'on' : 'off'}
            label="Show Player ID"
            textAlignment="left"
            defaultChecked={field.value}
            checked={field.value}
            onCheckedChange={field.onChange}
          />
        )}
      />
      <FormField
        control={control}
        name="header.isSticky"
        render={({ field }) => (
          <Switch
            key={field.value ? 'on' : 'off'}
            label="Sticky Header"
            textAlignment="left"
            defaultChecked={field.value}
            checked={field.value}
            onCheckedChange={field.onChange}
          />
        )}
      />
      <FormField
        control={control}
        name="header.backgroundOpacity"
        render={({ field: { onChange, value } }) => (
          <FormItem>
            <FormControl>
              <Input
                label="Background Opacity (%)"
                name="header.backgroundOpacity"
                type="number"
                placeholder="0"
                className="flex-row items-center justify-between"
                inputClassName="w-[102px] h-[30px] ml-9"
                labelClassName="mb-0"
                value={value}
                min={0}
                max={100}
                onChange={(e) => onChange(Number(e.target.value))}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        name="header.textColor"
        control={control}
        render={({ field: { value, onChange } }) => (
          <FormItem>
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Text color</label>
              <FormControl>
                <div className="[&_.MuiInputAdornment-root]:mr-0 [&_.MuiTextField-root]:!w-[102px] [&_.MuiInputBase-input]:!px-3 [&_.MuiInputBase-input]:!py-2 [&_.MuiInputBase-input]:!pl-0">
                  <AcGradientInput
                    name="header.textColor"
                    defaultValue={value || { colorOne: '#FFF' }}
                    setValue={colorSetValueHandler(onChange)}
                    onlySolid={true}
                    circular={true}
                    imgWidth="16px"
                    imgHeight="16px"
                    error={hasTextColorError}
                    helperText={textColorHelperText}
                  />
                </div>
              </FormControl>
            </div>
            <FormMessage />
          </FormItem>
        )}
      />
    </DesignBlockMenuItem>
  );
};
